import React, { useState, useEffect, useContext } from "react";
import { Tooltip, OverlayTrigger, Modal, Button, Row, Col, Card, Form } from 'react-bootstrap'
import { CheckCircle, RotateCcw, Trash, Users, Edit, Download } from "react-feather";
import axios from "axios";
import NotyfContext from "../contexts/NotyfContext";
import { formatInTimeZone } from "date-fns-tz";
import {
    convertDateToDisplay,
    NumberRangeColumnFilter,
    SelectColumnFilter,
    downloadQRCode, createQRCodePlatform02,
    getExcelFromObject,
    getCollectedParticipantsExportHeaders,
    getOriginLabel, parseFlexibleDate,
    normalizeDateRange,
    PANEL_EVENT_TIME_ZONE,
} from "../assets/functions.js";

import { processFormFields } from "./EventHistory01.js";

const CREDENTIAL_CHECKIN_FILTERS = {
    WITHOUT_CHECKIN: 0,
    CHECKIN_HISTORY: 1,
    ALL: 2,
    WITH_CHECKIN: 3,
    LAST_PER_DAY: 4,
    FIRST_CHECKIN: 6,
    MULTIPLE_CHECKINS: 7,
    NOT_RETURNED: 8,
};

const ON_SITE_REGISTRATION_WINDOW_MINUTES = 10;
const ON_SITE_REGISTRATION_WINDOW_OPTIONS = [10, 20, 30, 40, 50, 60];

export const PLATFORM_2_USER_ACTIONS = {
    DELETE: "delete",
    RESET_HISTORY: "resetHistory",
    RESET_SCORE_HISTORY: "resetScoreHistory",
};

const isParticipantUserType = (userType) => Number(userType) === 0;

export const buildPlatform2UserAction = ({
    dataTable,
    action,
    userId,
    userType = 0,
    scope,
}) => {
    const isParticipant = isParticipantUserType(userType);
    const actionProps = {
        platform: 2,
        evento_id: dataTable?.evento_id || dataTable?.event_id,
    };

    if (userId !== undefined && userId !== null) {
        actionProps.id = userId;
        actionProps.user_id = userId;
    }

    switch (action) {
        case PLATFORM_2_USER_ACTIONS.DELETE:
            return {
                ...actionProps,
                type: isParticipant ? "removeUsers" : "removeUsersMod",
            };
        case PLATFORM_2_USER_ACTIONS.RESET_HISTORY:
            return {
                ...actionProps,
                type: isParticipant ? "removeUsersHistory" : "removeUsersModHistory",
            };
        case PLATFORM_2_USER_ACTIONS.RESET_SCORE_HISTORY:
            return {
                ...actionProps,
                type: "resetScoreHistory",
                scope: scope === "single" ? "single" : "all",
                ...(userId !== undefined && userId !== null ? { user_id: userId } : {}),
            };
        default:
            throw new Error(`Unsupported Platform 2 user action: ${action}`);
    }
};

const cloneRows = (rows) => JSON.parse(JSON.stringify(rows || []));

const normalizeParticipantFilterText = (value) => {
    if (value === undefined || value === null) return "";
    return String(value).trim().replace(/\s+/g, " ");
};

const getComparableParticipantFilterValue = (value) => (
    normalizeParticipantFilterText(value).toLocaleLowerCase("pt-BR")
);

const parseCheckinDate = (value) => {
    if (!value) return null;
    const parsedDate = parseFlexibleDate(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const parseCheckinTimestamp = (value) => {
    const parsedDate = parseCheckinDate(value);
    return parsedDate ? parsedDate.getTime() : NaN;
};

const getComparableParticipantTimestamp = (value) => {
    if (!value) return null;

    const timestamp = parseCheckinTimestamp(value);
    return Number.isNaN(timestamp) ? null : timestamp;
};

const getFirstParticipantCheckinTimestamp = (participant) => {
    const checkinTimestamps = (Array.isArray(participant?.checkinHistory) ? participant.checkinHistory : [])
        .map((checkin) => getComparableParticipantTimestamp(checkin?.data_checkin))
        .filter((timestamp) => timestamp !== null);

    if (checkinTimestamps.length > 0) {
        return Math.min(...checkinTimestamps);
    }

    return getComparableParticipantTimestamp(participant?.data_checkin);
};

const getParticipantAttendanceType = (participant, registrationWindowMinutes = ON_SITE_REGISTRATION_WINDOW_MINUTES) => {
    const registrationTimestamp = getComparableParticipantTimestamp(participant?.register_date)
        ?? getComparableParticipantTimestamp(participant?.creation_date);
    const firstCheckinTimestamp = getFirstParticipantCheckinTimestamp(participant);
    const normalizedWindowMinutes = ON_SITE_REGISTRATION_WINDOW_OPTIONS.includes(Number(registrationWindowMinutes))
        ? Number(registrationWindowMinutes)
        : ON_SITE_REGISTRATION_WINDOW_MINUTES;

    if (registrationTimestamp !== null && firstCheckinTimestamp !== null) {
        const elapsedMilliseconds = firstCheckinTimestamp - registrationTimestamp;
        const registrationWindowMilliseconds = normalizedWindowMinutes * 60 * 1000;

        if (elapsedMilliseconds >= 0 && elapsedMilliseconds <= registrationWindowMilliseconds) {
            return 'Cadastro no local';
        }
    }

    return 'Check-in';
};

const compareParticipantsByDefaultOrder = (firstParticipant, secondParticipant) => {
    const firstCheckinTimestamp = getComparableParticipantTimestamp(firstParticipant?.data_checkin);
    const secondCheckinTimestamp = getComparableParticipantTimestamp(secondParticipant?.data_checkin);

    if (firstCheckinTimestamp !== null || secondCheckinTimestamp !== null) {
        if (firstCheckinTimestamp === null) return 1;
        if (secondCheckinTimestamp === null) return -1;
        if (firstCheckinTimestamp !== secondCheckinTimestamp) {
            return secondCheckinTimestamp - firstCheckinTimestamp;
        }
    }

    const firstCreationTimestamp = getComparableParticipantTimestamp(firstParticipant?.creation_date)
        ?? getComparableParticipantTimestamp(firstParticipant?.register_date);
    const secondCreationTimestamp = getComparableParticipantTimestamp(secondParticipant?.creation_date)
        ?? getComparableParticipantTimestamp(secondParticipant?.register_date);

    if (firstCreationTimestamp !== null || secondCreationTimestamp !== null) {
        if (firstCreationTimestamp === null) return 1;
        if (secondCreationTimestamp === null) return -1;
        if (firstCreationTimestamp !== secondCreationTimestamp) {
            return secondCreationTimestamp - firstCreationTimestamp;
        }
    }

    return String(firstParticipant?.email || "").localeCompare(String(secondParticipant?.email || ""), "pt-BR", {
        sensitivity: "base",
    });
};

const getCheckinFields = (participant) => ({
    has_checkin: participant?.has_checkin || (participant?.data_checkin ? 1 : 0),
    data_checkin: participant?.data_checkin || "",
    data_checkin_formatted: participant?.data_checkin ? convertDateToDisplay(participant.data_checkin) : "",
    checkin_id: participant?.checkin_id || participant?.id || null,
    checkin_origin: participant?.checkin_origin || "",
    checkin_ip: participant?.checkin_ip || "",
    checkin_location: participant?.checkin_location || "",
    has_signature: Number(participant?.has_signature) === 1 ? 1 : 0,
    signature_file_name: participant?.signature_file_name || "",
    signature_mime_type: participant?.signature_mime_type || "",
    /*
    signature_status: Number(participant?.has_signature) === 1 ? "Registrada" : "Não registrada",
    */
    signature_status: Number(participant?.has_signature) === 1 ? "Registrada" : "",
});

const buildParticipantCheckinHistory = (participant) => ({
    id: participant?.checkin_record_id ?? participant?.checkin_id ?? participant?.id ?? null,
    user_id: participant?.user_id ?? null,
    data_checkin: participant?.data_checkin || "",
    data_checkin_formatted: participant?.data_checkin ? convertDateToDisplay(participant.data_checkin) : "",
    checkin_origin: participant?.checkin_origin || "",
    checkin_ip: participant?.checkin_ip || "",
    checkin_location: participant?.checkin_location || "",
    has_signature: Number(participant?.has_signature) === 1 ? 1 : 0,
    signature_file_name: participant?.signature_file_name || "",
    signature_mime_type: participant?.signature_mime_type || "",
});

const getParticipantFieldValues = (participant, fieldKey) => {
    if (!fieldKey) return [];

    const rawValue = participant?.[fieldKey];
    const values = Array.isArray(rawValue) ? rawValue : [rawValue];

    return Array.from(new Set(values.map(normalizeParticipantFilterText).filter(Boolean)));
};

const matchesParticipantFieldValue = (participant, fieldKey, fieldValue) => {
    if (!fieldKey || !fieldValue) return true;

    const comparableValue = getComparableParticipantFilterValue(fieldValue);

    return getParticipantFieldValues(participant, fieldKey).some(
        (participantValue) => getComparableParticipantFilterValue(participantValue) === comparableValue
    );
};

const getParticipantFieldOptions = (dataTable) => (
    (dataTable?.json?.form?.fields || []).map((field) => ({
        value: String(field.inputID),
        label: field.label || `Campo ${field.inputID}`,
    }))
);

const getParticipantValueOptions = (participants, selectedField) => {
    if (!selectedField) return [];

    const uniqueValues = new Map();

    participants.forEach((participant) => {
        getParticipantFieldValues(participant, selectedField).forEach((value) => {
            const comparableValue = getComparableParticipantFilterValue(value);
            if (comparableValue && !uniqueValues.has(comparableValue)) {
                uniqueValues.set(comparableValue, value);
            }
        });
    });

    return Array.from(uniqueValues.values())
        .sort((firstValue, secondValue) => firstValue.localeCompare(secondValue, "pt-BR", { sensitivity: "base", numeric: true }))
        .map((value) => ({ value, label: value }));
};

const parseParticipantDetails = (value) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
        return value;
    }

    if (typeof value !== "string" || !value.trim()) {
        return {};
    }

    try {
        const parsedValue = JSON.parse(value);
        return parsedValue && typeof parsedValue === "object" && !Array.isArray(parsedValue)
            ? parsedValue
            : {};
    } catch (error) {
        return {};
    }
};

const getParticipantDetailValue = (detail) => (
    detail && typeof detail === "object" && !Array.isArray(detail) && Object.prototype.hasOwnProperty.call(detail, "value")
        ? detail.value
        : detail
);

const getCollectedParticipantFields = (dataTable) => {
    const form = dataTable?.json?.form || {};
    const fields = Array.isArray(form.fields) ? form.fields : [];
    const fieldsToShow = Array.isArray(form.fieldsToShow) ? form.fieldsToShow : [];
    const fieldsByInputID = new Map(
        fields
            .filter((field) => field && field.inputID !== undefined && field.inputID !== null)
            .map((field) => [String(field.inputID), field]),
    );

    return fieldsToShow
        .map((fieldToShow) => {
            const inputID = fieldToShow && typeof fieldToShow === "object"
                ? fieldToShow.inputID
                : fieldToShow;

            if (inputID === undefined || inputID === null) return null;

            return fieldsByInputID.get(String(inputID))
                || (fieldToShow && typeof fieldToShow === "object" ? fieldToShow : null);
        })
        .filter(Boolean);
};

const formatCollectedParticipantValue = (value) => {
    const normalizedValue = getParticipantDetailValue(value);

    if (Array.isArray(normalizedValue)) {
        return normalizedValue
            .map((item) => (item && typeof item === "object" ? JSON.stringify(item) : String(item)))
            .join(", ");
    }

    if (normalizedValue && typeof normalizedValue === "object") {
        return JSON.stringify(normalizedValue);
    }

    return normalizedValue === null || normalizedValue === undefined ? "" : String(normalizedValue);
};

const getCollectedParticipantDisplayFields = (participant, fields) => {
    const participantDetails = parseParticipantDetails(participant?.json);

    return fields
        .map((field) => {
            const inputID = String(field.inputID);
            const displayValue = formatCollectedParticipantValue(participantDetails[inputID]);

            return displayValue === ""
                ? null
                : {
                    inputID,
                    label: field.label || field.name || inputID,
                    displayValue,
                };
        })
        .filter(Boolean);
};

const isCheckinWithinDateRange = (value, dateRange) => {
    const normalizedRange = normalizeDateRange(dateRange);
    if (!normalizedRange.valid) return false;
    if (!normalizedRange.startDate && !normalizedRange.endDate) return true;

    const checkinDate = parseCheckinDate(value);
    if (!checkinDate) return false;

    if (normalizedRange.startDate && checkinDate < normalizedRange.startDate) return false;
    if (normalizedRange.endDate && checkinDate > normalizedRange.endDate) return false;

    return true;
};

const sameSaoPauloDate = (firstDate, secondDate) => (
    formatInTimeZone(firstDate, PANEL_EVENT_TIME_ZONE, "yyyy-MM-dd")
    === formatInTimeZone(secondDate, PANEL_EVENT_TIME_ZONE, "yyyy-MM-dd")
);

const buildCredentialChartData = (checkins, dateRange) => {
    const normalizedRange = normalizeDateRange(dateRange);
    if (!normalizedRange.valid) {
        return {
            labels: [],
            datasets: [{
                label: "Check-ins",
                data: [],
                backgroundColor: "#198754",
                borderWidth: 0,
            }],
        };
    }

    const chartBuckets = new Map();
    const hasStartDate = Boolean(normalizedRange.startDate);
    const hasEndDate = Boolean(normalizedRange.endDate);
    const isSingleDayRange = Boolean(hasStartDate && hasEndDate && sameSaoPauloDate(normalizedRange.startDate, normalizedRange.endDate));
    let chartLabels = [];
    let chartData = [];

    if (isSingleDayRange) {
        chartLabels = Array.from({ length: 24 }, (_, index) => `${String(index).padStart(2, "0")}:00`);
        chartData = Array(24).fill(0);
    }

    checkins.forEach((participant) => {
        if (!participant.data_checkin) return;
        if (!isCheckinWithinDateRange(participant.data_checkin, normalizedRange)) return;

        const checkinDate = parseCheckinDate(participant.data_checkin);
        if (!checkinDate) return;

        if (isSingleDayRange) {
            if (sameSaoPauloDate(checkinDate, normalizedRange.startDate)) {
                chartData[Number(formatInTimeZone(checkinDate, PANEL_EVENT_TIME_ZONE, "H"))] += 1;
            }
            return;
        }

        const dateKey = formatInTimeZone(checkinDate, PANEL_EVENT_TIME_ZONE, "yyyy-MM-dd");
        chartBuckets.set(dateKey, (chartBuckets.get(dateKey) || 0) + 1);
    });

    if (!isSingleDayRange) {
        const sortedKeys = Array.from(chartBuckets.keys()).sort((firstDate, secondDate) => firstDate.localeCompare(secondDate));
        chartLabels = sortedKeys.map((dateKey) => {
            const [year, month, day] = dateKey.split("-").map(Number);
            return formatInTimeZone(new Date(year, month - 1, day, 12), PANEL_EVENT_TIME_ZONE, "dd/MM");
        });
        chartData = sortedKeys.map((dateKey) => chartBuckets.get(dateKey));
    }

    return {
        labels: chartLabels,
        datasets: [
            {
                label: "Check-ins",
                data: chartData,
                backgroundColor: "#198754",
                borderWidth: 0,
            },
        ],
    };
};

const buildCheckinMetricLists = (checkins, participantsWithCheckin) => {
    const lastPerDayMap = new Map();
    checkins.forEach((entry) => {
        const checkinDate = parseCheckinDate(entry.data_checkin);
        if (!checkinDate) return;

        const dayKey = formatInTimeZone(checkinDate, PANEL_EVENT_TIME_ZONE, "yyyy-MM-dd");
        const key = `${entry.user_id}__${dayKey}`;
        const existing = lastPerDayMap.get(key);
        if (!existing || parseCheckinTimestamp(entry.data_checkin) > parseCheckinTimestamp(existing.data_checkin)) {
            lastPerDayMap.set(key, entry);
        }
    });

    const firstCheckinMap = new Map();
    checkins.forEach((entry) => {
        const existing = firstCheckinMap.get(entry.user_id);
        if (!existing || parseCheckinTimestamp(entry.data_checkin) < parseCheckinTimestamp(existing.data_checkin)) {
            firstCheckinMap.set(entry.user_id, entry);
        }
    });

    const scanCountMap = new Map();
    checkins.forEach((entry) => {
        scanCountMap.set(entry.user_id, (scanCountMap.get(entry.user_id) || 0) + 1);
    });

    const usersWithMultipleCheckins = new Set(
        Array.from(scanCountMap.entries())
            .filter(([, count]) => count > 1)
            .map(([userId]) => userId)
    );

    const multipleCheckinsList = Array.from(
        checkins
            .filter((entry) => usersWithMultipleCheckins.has(entry.user_id))
            .reduce((map, entry) => {
                const existing = map.get(entry.user_id);
                if (!existing || parseCheckinTimestamp(entry.data_checkin) > parseCheckinTimestamp(existing.data_checkin)) {
                    map.set(entry.user_id, entry);
                }
                return map;
            }, new Map())
            .values()
    );

    const notReturnedList = participantsWithCheckin.filter(
        (participant) => !usersWithMultipleCheckins.has(participant.user_id)
    );

    return {
        lastPerDayList: Array.from(lastPerDayMap.values()),
        firstCheckinList: Array.from(firstCheckinMap.values()),
        multipleCheckinsList,
        notReturnedList,
        usersWithMultipleCheckins,
    };
};

const buildCredentialCheckinReport = (
    dataTable,
    processedParticipants,
    checkinsData,
    filterParticipants,
    filterCredentialCharts,
    processParticipant,
    isWithinCreationDateRange
) => {
    const baseParticipants = processedParticipants.filter((participant) => participant.type === 0);
    const baseParticipantsByUserId = new Map(baseParticipants.map((participant) => [participant.user_id, participant]));
    const processedLatestRows = cloneRows(checkinsData?.allUsers).map(processParticipant);
    const latestRowsByUserId = new Map(processedLatestRows.map((participant) => [participant.user_id, participant]));
    const processedCheckins = cloneRows(checkinsData?.allCheckins).map((participant) => {
        const processedParticipant = processParticipant(participant);
        return {
            ...(baseParticipantsByUserId.get(processedParticipant.user_id) || {}),
            ...processedParticipant,
            checkin_record_id: processedParticipant.checkin_id || processedParticipant.id,
        };
    });

    const checkinHistoryByUserId = processedCheckins.reduce((map, participant) => {
        if (!map.has(participant.user_id)) {
            map.set(participant.user_id, []);
        }

        map.get(participant.user_id).push(buildParticipantCheckinHistory(participant));
        return map;
    }, new Map());

    checkinHistoryByUserId.forEach((history, userId) => {
        checkinHistoryByUserId.set(userId, [...history].sort((firstCheckin, secondCheckin) => {
            const firstTimestamp = parseCheckinTimestamp(firstCheckin?.data_checkin);
            const secondTimestamp = parseCheckinTimestamp(secondCheckin?.data_checkin);

            if (secondTimestamp !== firstTimestamp) {
                return secondTimestamp - firstTimestamp;
            }

            return (secondCheckin?.id || 0) - (firstCheckin?.id || 0);
        }));
    });

    const registrationWindowMinutes = ON_SITE_REGISTRATION_WINDOW_OPTIONS.includes(Number(filterParticipants?.registrationWindowMinutes))
        ? Number(filterParticipants.registrationWindowMinutes)
        : ON_SITE_REGISTRATION_WINDOW_MINUTES;
    const normalizedAllUsers = baseParticipants.map((participant) => {
        const participantWithHistory = {
            ...participant,
            ...getCheckinFields(latestRowsByUserId.get(participant.user_id)),
            checkinHistory: checkinHistoryByUserId.get(participant.user_id) || [],
        };

        return {
            ...participantWithHistory,
            attendance_type: getParticipantAttendanceType(participantWithHistory, registrationWindowMinutes),
        };
    });

    const normalizedAllCheckins = processedCheckins.map((participant) => {
        const participantWithHistory = {
            ...participant,
            checkinHistory: checkinHistoryByUserId.get(participant.user_id) || [],
        };

        return {
            ...participantWithHistory,
            attendance_type: getParticipantAttendanceType(participantWithHistory, registrationWindowMinutes),
        };
    });

    const statsCheckedIn = normalizedAllUsers.filter((participant) => !!participant.data_checkin).length;
    const statsTotal = normalizedAllUsers.length;
    const statsMissing = Math.max(0, statsTotal - statsCheckedIn);
    const statsRate = statsTotal > 0 ? Math.round((statsCheckedIn / statsTotal) * 100) : 0;

    const chartFilter = filterCredentialCharts || {};
    const participantFieldOptions = getParticipantFieldOptions(dataTable);
    const selectedParticipantField = participantFieldOptions.some((option) => option.value === chartFilter.participantFieldFilter)
        ? chartFilter.participantFieldFilter
        : null;
    const participantValueOptions = getParticipantValueOptions(normalizedAllUsers, selectedParticipantField);
    const participantValueLookup = new Map(
        participantValueOptions.map((option) => [getComparableParticipantFilterValue(option.value), option.value])
    );
    const selectedParticipantValue = participantValueLookup.get(
        getComparableParticipantFilterValue(chartFilter.participantValueFilter)
    ) || null;
    const hasChartParticipantFilter = Boolean(selectedParticipantField && selectedParticipantValue);
    const selectedCheckinFilter = Number(filterParticipants.checkin_filter ?? CREDENTIAL_CHECKIN_FILTERS.ALL);
    const tableMode = filterParticipants.participantReportMode === "checkins" ? "checkins" : "records";
    const tableCheckinDateRange = selectedCheckinFilter === CREDENTIAL_CHECKIN_FILTERS.ALL
        ? {}
        : (filterParticipants.checkinDateRange || {});
    const normalizedTableCheckinDateRange = normalizeDateRange(tableCheckinDateRange);
    const hasTableCheckinDateRange = normalizedTableCheckinDateRange.valid
        && Boolean(normalizedTableCheckinDateRange.startDate || normalizedTableCheckinDateRange.endDate);

    const registeredUsers = normalizedAllUsers.filter((participant) => isWithinCreationDateRange(participant, filterParticipants));
    const tableBaseUsers = tableMode === "checkins" ? normalizedAllUsers : registeredUsers;
    const tableBaseUserIds = new Set(tableBaseUsers.map((participant) => participant.user_id));
    const tableBaseCheckins = normalizedAllCheckins.filter((participant) => tableBaseUserIds.has(participant.user_id));

    const tableCheckinsInRange = tableBaseCheckins.filter((participant) => isCheckinWithinDateRange(participant.data_checkin, normalizedTableCheckinDateRange));

    const chartUsers = hasChartParticipantFilter
        ? normalizedAllUsers.filter((participant) => matchesParticipantFieldValue(participant, selectedParticipantField, selectedParticipantValue))
        : normalizedAllUsers;
    const chartUserIds = new Set(chartUsers.map((participant) => participant.user_id));
    const chartCheckins = normalizedAllCheckins.filter((participant) => chartUserIds.has(participant.user_id));
    const normalizedChartDateRange = normalizeDateRange(chartFilter.dateRange);
    const chartCheckinsInRange = chartCheckins.filter((participant) => isCheckinWithinDateRange(participant.data_checkin, normalizedChartDateRange));
    const hasChartDateRange = normalizedChartDateRange.valid
        && Boolean(normalizedChartDateRange.startDate || normalizedChartDateRange.endDate);
    const isSingleDayChartRange = Boolean(
        normalizedChartDateRange.valid
        && normalizedChartDateRange.startDate
        && normalizedChartDateRange.endDate
        && sameSaoPauloDate(normalizedChartDateRange.startDate, normalizedChartDateRange.endDate)
    );

    const latestCheckinInRangeByUserId = tableCheckinsInRange.reduce((map, participant) => {
        const existingParticipant = map.get(participant.user_id);
        const participantTimestamp = parseCheckinTimestamp(participant.data_checkin);
        const existingParticipantTimestamp = parseCheckinTimestamp(existingParticipant?.data_checkin);

        if (!existingParticipant || participantTimestamp > existingParticipantTimestamp) {
            map.set(participant.user_id, participant);
        }

        return map;
    }, new Map());

    const participantsWithCheckinInRange = tableBaseUsers
        .filter((participant) => latestCheckinInRangeByUserId.has(participant.user_id))
        .map((participant) => latestCheckinInRangeByUserId.get(participant.user_id) || participant);
    const participantsWithAnyCheckin = tableBaseUsers.filter((participant) => participant.data_checkin);
    const participantsWithoutAnyCheckin = tableBaseUsers
        .filter((participant) => !participant.data_checkin)
        .map((participant) => ({
            ...participant,
            ...getCheckinFields({}),
        }));
    const scopedUsers = hasTableCheckinDateRange ? participantsWithCheckinInRange : tableBaseUsers;
    const presentListForSelectedRange = hasTableCheckinDateRange ? participantsWithCheckinInRange : participantsWithAnyCheckin;
    const absentListForSelectedRange = participantsWithoutAnyCheckin;
    const tableMetrics = buildCheckinMetricLists(tableCheckinsInRange, presentListForSelectedRange);
    const rawParticipantsWithAnyCheckin = normalizedAllUsers.filter((participant) => participant.data_checkin);
    const rawParticipantsWithoutAnyCheckin = normalizedAllUsers
        .filter((participant) => !participant.data_checkin)
        .map((participant) => ({
            ...participant,
            ...getCheckinFields({}),
        }));
    const rawMetrics = buildCheckinMetricLists(normalizedAllCheckins, rawParticipantsWithAnyCheckin);

    let listOfUsers;
    if (tableMode === "records") {
        listOfUsers = registeredUsers;
    } else {
        switch (selectedCheckinFilter) {
            case CREDENTIAL_CHECKIN_FILTERS.WITHOUT_CHECKIN:
                listOfUsers = absentListForSelectedRange;
                break;
            case CREDENTIAL_CHECKIN_FILTERS.CHECKIN_HISTORY:
                listOfUsers = tableCheckinsInRange;
                break;
            case CREDENTIAL_CHECKIN_FILTERS.WITH_CHECKIN:
                listOfUsers = presentListForSelectedRange;
                break;
            case CREDENTIAL_CHECKIN_FILTERS.LAST_PER_DAY:
                listOfUsers = tableMetrics.lastPerDayList;
                break;
            case CREDENTIAL_CHECKIN_FILTERS.FIRST_CHECKIN:
                listOfUsers = tableMetrics.firstCheckinList;
                break;
            case CREDENTIAL_CHECKIN_FILTERS.MULTIPLE_CHECKINS:
                listOfUsers = tableMetrics.multipleCheckinsList;
                break;
            case CREDENTIAL_CHECKIN_FILTERS.NOT_RETURNED:
                listOfUsers = tableMetrics.notReturnedList;
                break;
            case CREDENTIAL_CHECKIN_FILTERS.ALL:
            default:
                listOfUsers = scopedUsers;
                break;
        }
    }

    const latestChartCheckinInRangeByUserId = chartCheckinsInRange.reduce((map, participant) => {
        const existingParticipant = map.get(participant.user_id);
        const participantTimestamp = parseCheckinTimestamp(participant.data_checkin);
        const existingParticipantTimestamp = parseCheckinTimestamp(existingParticipant?.data_checkin);

        if (!existingParticipant || participantTimestamp > existingParticipantTimestamp) {
            map.set(participant.user_id, participant);
        }

        return map;
    }, new Map());
    const chartUsersWithCheckinInRange = chartUsers
        .filter((participant) => latestChartCheckinInRangeByUserId.has(participant.user_id))
        .map((participant) => latestChartCheckinInRangeByUserId.get(participant.user_id) || participant);
    const chartUsersWithAnyCheckin = chartUsers.filter((participant) => participant.data_checkin);
    const chartUsersWithoutAnyCheckin = chartUsers.filter((participant) => !participant.data_checkin);
    const chartScopedUsers = chartUsers;
    const withCheckinForChart = hasChartDateRange ? chartUsersWithCheckinInRange.length : chartUsersWithAnyCheckin.length;
    const withoutCheckinForChart = hasChartDateRange
        ? Math.max(0, chartUsers.length - chartUsersWithCheckinInRange.length)
        : chartUsersWithoutAnyCheckin.length;
    const pieChartTitle = isSingleDayChartRange
        ? "Participantes no dia"
        : hasChartDateRange
            ? "Participantes no período"
            : "Total de participantes";
    const timeChartTitle = isSingleDayChartRange ? "Total de check-ins no dia" : "Total de check-ins";

    const pieChartData = {
        labels: [`Presentes (${withCheckinForChart})`, `Ausentes (${withoutCheckinForChart})`],
        datasets: [
            {
                data: [withCheckinForChart, withoutCheckinForChart],
                backgroundColor: ["#198754", "#dc3545"],
                borderWidth: 0,
            },
        ],
    };

    const filterOptions = [
        { value: CREDENTIAL_CHECKIN_FILTERS.ALL, label: `Todos (${normalizedAllUsers.length})` },
        { value: CREDENTIAL_CHECKIN_FILTERS.WITH_CHECKIN, label: `Presentes (${rawParticipantsWithAnyCheckin.length})` },
        { value: CREDENTIAL_CHECKIN_FILTERS.WITHOUT_CHECKIN, label: `Ausentes (${rawParticipantsWithoutAnyCheckin.length})` },
        { value: CREDENTIAL_CHECKIN_FILTERS.CHECKIN_HISTORY, label: `Histórico de Check-ins (${normalizedAllCheckins.length})` },
        { value: CREDENTIAL_CHECKIN_FILTERS.LAST_PER_DAY, label: `Último Check-in por Dia (${rawMetrics.lastPerDayList.length})` },
        { value: CREDENTIAL_CHECKIN_FILTERS.FIRST_CHECKIN, label: `Primeiro Check-in por Pessoa (${rawMetrics.firstCheckinList.length})` },
        { value: CREDENTIAL_CHECKIN_FILTERS.MULTIPLE_CHECKINS, label: `Múltiplos Check-ins (${rawMetrics.multipleCheckinsList.length})` },
        { value: CREDENTIAL_CHECKIN_FILTERS.NOT_RETURNED, label: `Não Retornaram (${rawMetrics.notReturnedList.length})` },
    ];
    const filterOptionGroups = [
        {
            label: "Filtros principais",
            options: filterOptions.filter((option) => [
                CREDENTIAL_CHECKIN_FILTERS.ALL,
                CREDENTIAL_CHECKIN_FILTERS.WITH_CHECKIN,
                CREDENTIAL_CHECKIN_FILTERS.WITHOUT_CHECKIN,
                CREDENTIAL_CHECKIN_FILTERS.CHECKIN_HISTORY,
            ].includes(option.value)),
        },
        {
            label: "Filtros avançados",
            options: filterOptions.filter((option) => [
                CREDENTIAL_CHECKIN_FILTERS.LAST_PER_DAY,
                CREDENTIAL_CHECKIN_FILTERS.FIRST_CHECKIN,
                CREDENTIAL_CHECKIN_FILTERS.MULTIPLE_CHECKINS,
                CREDENTIAL_CHECKIN_FILTERS.NOT_RETURNED,
            ].includes(option.value)),
        },
    ];

    return {
        participants: [...listOfUsers].sort(compareParticipantsByDefaultOrder),
        allParticipants: normalizedAllUsers,
        allUsersTotal: normalizedAllUsers.length,
        allCheckinsTotal: normalizedAllCheckins.length,
        currentCount: listOfUsers.length,
        tableMode,
        filterOptions,
        filterOptionGroups,
        participantFilter: {
            field: selectedParticipantField,
            value: selectedParticipantValue,
            isActive: hasChartParticipantFilter,
        },
        participantFilterOptions: {
            fields: participantFieldOptions,
            values: participantValueOptions,
        },
        stats: {
            registered: statsTotal,
            checkedIn: statsCheckedIn,
            missing: statsMissing,
            rate: statsRate,
        },
        chartData: {
            pieChart: pieChartData,
            hourlyChart: buildCredentialChartData(chartCheckins, chartFilter.dateRange),
            pieChartTitle,
            pieChartParticipants: chartScopedUsers.length,
            chartTitle: timeChartTitle,
            checkinsInRange: chartCheckinsInRange.length,
        },
    };
};

export function processData02(dataTable, data, filterParticipants, filterUsers, filterCredentialCharts = {}, filterCheckins = filterParticipants) {
    const allParticipants = cloneRows(data.participants);

    const getUserType = (type) => {
        switch (type) {
            case 0:
                return 'Comum'
            case 1:
                return 'Gestor'
            case 2:
                return 'Expositor'
            default:
                break;
        }
    }

    const isWithinCreationDateRange = (item, currentFilter) => {
        const normalizedRange = normalizeDateRange(currentFilter?.dateRange);
        if (!normalizedRange.valid) {
            return false;
        }

        if (!normalizedRange.startDate && !normalizedRange.endDate) {
            return true;
        }

        if (!item.creation_date) {
            return false;
        }

        const creationDate = parseCheckinDate(item.creation_date);
        if (!creationDate) {
            return false;
        }

        const { startDate, endDate } = normalizedRange;

        if (startDate && endDate) {
            return creationDate >= startDate && creationDate <= endDate;
        } else if (startDate) {
            return creationDate >= startDate;
        } else if (endDate) {
            return creationDate <= endDate;
        }

        return true;
    };

    // Function to process participants
    const processParticipant = (participant) => {
        const rsvp = participant.rsvp;
        const registerDate = participant.register_date;
        participant.participantDetails = parseParticipantDetails(participant.json);

        // Adding formatted values
        participant.participantDetails.creation_date_formatted = {
            'label': 'creation_date_formatted',
            'value': participant.creation_date ? convertDateToDisplay(participant.creation_date) : ''
        };
        participant.participantDetails.register_date_formatted = {
            'label': 'register_date_formatted',
            'value': participant.register_date ? convertDateToDisplay(participant.register_date) : ''
        };
        participant.participantDetails.access_code_expires_formatted = {
            'label': 'access_code_expires_formatted',
            'value': participant.access_code_expires ? convertDateToDisplay(participant.access_code_expires) : ''
        };

        participant.participantDetails.last_access_formatted = {
            'label': 'last_access_formatted',
            'value': participant.last_access ? convertDateToDisplay(participant.last_access) : ''
        };
        participant.participantDetails.data_checkin_formatted = {
            'label': 'data_checkin_formatted',
            'value': participant.data_checkin ? convertDateToDisplay(participant.data_checkin) : ''
        };
        participant.participantDetails.checkin_origin = {
            'label': 'checkin_origin',
            'value': participant.checkin_origin || ''
        };
        participant.participantDetails.checkin_ip = {
            'label': 'checkin_ip',
            'value': participant.checkin_ip || ''
        };
        participant.participantDetails.checkin_location = {
            'label': 'checkin_location',
            'value': participant.checkin_location || ''
        };
        participant.participantDetails.signature_status = {
            'label': 'signature_status',
            /*
            'value': Number(participant.has_signature) === 1 ? 'Registrada' : 'Não registrada'
            */
            'value': Number(participant.has_signature) === 1 ? 'Registrada' : ''
        };
        participant.participantDetails.status_formatted = {
            'label': 'status_formatted',
            'value': participant.status === 0 ? 'Inativo' : 'Ativo'
        };
        participant.participantDetails.type_formatted = {
            'label': 'type_formatted',
            'value': getUserType(participant.type)
        };
        participant.participantDetails.push_subscription = {
            'label': 'push_subscription',
            'value': participant.push_subscription !== null && participant.push_subscription !== '' ? 'Ativado' : 'Desativado'
        };
        participant.participantDetails.image = {
            'label': 'image',
            'value': participant.image ? dataTable.json.server.imageServer + "/plataforma-eventos/" + dataTable.event_id + "/" + participant.image : ''
        };

        // Add exhibitor-related information for users with type 1 or 2
        if (participant.type === 1 || participant.type === 2) {
            participant.participantDetails.total_users_collected = {
                'label': 'total_users_collected',
                'value': participant.total_users_collected || 0
            };
            participant.participantDetails.exhibitor_name = {
                'label': 'exhibitor_name',
                'value': participant.exhibitor_name ? participant.exhibitor_name.replace(/"/g, '') : ''
            };
        }

        // Transforming details
        const transformedDetails = {};
        for (const key in participant.participantDetails) {
            const detail = participant.participantDetails[key];
            transformedDetails[key] = getParticipantDetailValue(detail);
        }

        // Assign transformed details back to participant
        Object.assign(participant, transformedDetails);
        participant.rsvp = rsvp;
        participant.register_date = registerDate;

        const fields = participant.type > 0
            ? dataTable.json.form.fieldsUsers
            : dataTable.json.form.fields

        // Add dependency validation
        processFormFields(participant, dataTable, fields);

        return participant;
    };

    const processedParticipants = allParticipants.map((participant) => processParticipant(participant));
    const checkinsAvailable = Array.isArray(data.checkins?.allUsers) && Array.isArray(data.checkins?.allCheckins);
    const credentialReport = checkinsAvailable
        ? buildCredentialCheckinReport(
            dataTable,
            processedParticipants,
            data.checkins,
            { ...filterCheckins, participantReportMode: "checkins" },
            filterCredentialCharts,
            processParticipant,
            isWithinCreationDateRange
        )
        : null;

    // Arrays to hold participants based on type
    const participantsHistory = (credentialReport?.allParticipants || processedParticipants).filter((participant) => (
            participant.type === 0 && isWithinCreationDateRange(participant, filterParticipants)
        )).sort((firstParticipant, secondParticipant) => (
            (getComparableParticipantTimestamp(secondParticipant.creation_date) || 0)
            - (getComparableParticipantTimestamp(firstParticipant.creation_date) || 0)
            || String(firstParticipant.email || "").localeCompare(String(secondParticipant.email || ""), "pt-BR")
        ));
    const usersHistory = processedParticipants.filter((participant) => (
        participant.type > 0 && isWithinCreationDateRange(participant, filterUsers)
    ));
    const registrationHistory = processedParticipants.filter((participant) => (
        participant.type === 0
            ? isWithinCreationDateRange(participant, filterParticipants)
            : isWithinCreationDateRange(participant, filterUsers)
    ));
    const totalUsersCount = allParticipants.filter(p => p.type > 0).length;
    const totalParticipantsCount = allParticipants.filter(p => p.type === 0).length;
    const rankingParticipants = processedParticipants.filter((participant) => (
        participant.type === 0 && Number(participant.status) === 1
    )).sort((firstParticipant, secondParticipant) => (
        // rank_position is assigned by the Platform 2 score service. The panel
        // must not infer a ranking from the displayed total score.
        Number(firstParticipant.rank_position || Number.MAX_SAFE_INTEGER) - Number(secondParticipant.rank_position || Number.MAX_SAFE_INTEGER)
        || Number(firstParticipant.user_id || 0) - Number(secondParticipant.user_id || 0)
    ));

    return {
        'participants': participantsHistory,
        'allParticipants': credentialReport?.allParticipants || processedParticipants.filter((participant) => participant.type === 0),
        'checkinsAvailable': checkinsAvailable,
        'rankingParticipants': rankingParticipants,
        'users': usersHistory,
        'totalUsers': totalUsersCount,
        'totalParticipants': totalParticipantsCount,
        'history': registrationHistory,
        'checkinReport': credentialReport,
        'results': []
    };
}

export function setHeaders02(dataTable, setFields, handleDelete, handleEdit, filter, setShowImageModal, canEdit, handleCheckin, setFilterParticipants = null) {
    const newFields = [];
    const isCheckinsMode = filter.participantReportMode === "checkins";
    const signatureEnabled = dataTable.json?.credentialAccess?.apresentation?.afterCheckin?.signature?.status === true;
    const deletesSingleCheckin = Number(filter.checkin_filter) === CREDENTIAL_CHECKIN_FILTERS.CHECKIN_HISTORY;
    const registrationWindowMinutes = ON_SITE_REGISTRATION_WINDOW_OPTIONS.includes(Number(filter.registrationWindowMinutes))
        ? Number(filter.registrationWindowMinutes)
        : ON_SITE_REGISTRATION_WINDOW_MINUTES;
    const showEmailColumn = dataTable.json.form.showEmailInUserReport === true;
    if (filter.extraPropsParticipants) {
        newFields.push({ Header: "ID", accessor: "user_id" });
        newFields.push({ Header: "ID Externo", accessor: "external_user_id" });
        newFields.push({
            Header: "Origem",
            accessor: "origin",
            Filter: SelectColumnFilter,
            filter: "equals",
            renderFilterLabel: getOriginLabel,
            Cell: ({ value }) => value ? getOriginLabel(value) : "—",
            exportValue: (_, value) => getOriginLabel(value) || "—",
        });
    }
    if (showEmailColumn) {
        newFields.push({ Header: dataTable.json.form.emailLabel ? dataTable.json.form.emailLabel : 'E-mail', accessor: "email" });
    }
    if (isCheckinsMode) {
        newFields.push({ Header: "Data do Check-in", accessor: "data_checkin_formatted" });
        newFields.push({ Header: "Dispositivo do check-in", accessor: "checkin_origin", Filter: SelectColumnFilter, filter: "equals" });
        if (filter.extraPropsParticipants) {
            newFields.push({ Header: "IP do check-in", accessor: "checkin_ip" });
            newFields.push({ Header: "Localização do check-in", accessor: "checkin_location" });
        }
    }

    if (isCheckinsMode) {
        newFields.push({
            Header: "Tipo de entrada",
            accessor: "attendance_type",
            Filter: () => (
                <Form.Select
                    value={registrationWindowMinutes}
                    onChange={(event) => {
                        if (!setFilterParticipants) return;
                        setFilterParticipants((previousFilter) => ({
                            ...previousFilter,
                            registrationWindowMinutes: Number(event.target.value),
                        }));
                    }}
                    aria-label="Janela para cadastro no local"
                >
                    {ON_SITE_REGISTRATION_WINDOW_OPTIONS.map((minutes) => (
                        <option key={minutes} value={minutes}>{minutes} minutos</option>
                    ))}
                </Form.Select>
            )
        });
    }

    if (signatureEnabled && isCheckinsMode) {
        newFields.push({ Header: "Assinatura", accessor: "signature_status" });
    }

    dataTable.json.form.fields.forEach((element) => {
        if (!element.showInUserReport && !filter.extraPropsParticipants) {
            return;
        }
        let maxLength = 40;
        let truncatedString = element.label.length > maxLength
            ? element.label.slice(0, maxLength - 3) + "..."
            : element.label;

        switch (element.type) {
            case 'textarea':
            case 'input':
                if (element.validation === 'number') {
                    newFields.push({
                        Header: truncatedString,
                        accessor: String(element.inputID),
                        Filter: NumberRangeColumnFilter,
                        filter: "between",
                    });
                } else {
                    newFields.push({
                        Header: truncatedString,
                        accessor: String(element.inputID),
                    });
                }
                break;
            case 'checkbox':
            case 'select':
                newFields.push({
                    Header: truncatedString,
                    accessor: String(element.inputID),
                    Filter: SelectColumnFilter,
                    filter: "equals",
                    Cell: ({ value }) => {
                        if (Array.isArray(value) && value.length > 0) {
                            return (
                                <div>
                                    {value.map((item, index) => (
                                        <div key={index} style={{ marginBottom: '2px', display: 'flex', alignItems: 'flex-start' }}>
                                            <span style={{ marginRight: '6px', fontWeight: 'bold' }}>&bull;</span>
                                            <span>{item}</span>
                                        </div>
                                    ))}
                                </div>
                            );
                        } else {
                            return value || '';
                        }
                    }
                });
                break;
            default:
                newFields.push({
                    Header: truncatedString,
                    accessor: String(element.inputID),
                });
                break;
        }
    });

    newFields.push({ Header: "Status", accessor: "status_formatted", Filter: SelectColumnFilter, filter: "equals" });

    if (!isCheckinsMode) {
        newFields.push({ Header: "Data de criação", accessor: "creation_date_formatted" });
        newFields.push({ Header: "Data de registro", accessor: "register_date_formatted" });
    }
    newFields.push({
        Header: "Foto de perfil", accessor: "image", Cell: (cell) => {
            if (cell.row.original.image) {
                return (
                    <a onClick={(e) => { e.preventDefault(); setShowImageModal({ url: cell.row.original.image, show: true }) }} href={cell.row.original.image} rel="noreferrer">Foto</a>
                )
            } else {
                return ""
            }
        }
    });
    if (filter.extraPropsParticipants) {
        newFields.push({ Header: "Ranking", accessor: "rank_position" });
        newFields.push({ Header: "Pontuação", accessor: "total_value" });
        newFields.push({ Header: "Expositores coletados", accessor: "total_exhibitors_collected" });
        newFields.push({ Header: "Código de acesso", accessor: "access_code" });
        newFields.push({ Header: "Último acesso", accessor: "last_access_formatted" });
        newFields.push({ Header: "Último código de acesso", accessor: "access_code_used" });
        newFields.push({ Header: "Expiração do código de acesso", accessor: "access_code_expires_formatted" });
        newFields.push({ Header: "Notificação", accessor: "push_subscription" });
    }
    newFields.push({
        Header: "",
        accessor: 'icons',
        disableSortBy: true,
        Filter: false,
        Cell: (cell) => {
            const participant = cell.row.original;
            const props = buildPlatform2UserAction({
                dataTable,
                action: PLATFORM_2_USER_ACTIONS.DELETE,
                userId: participant.user_id,
                userType: participant.type,
            });
            const checkinRecordId = participant.checkin_record_id || participant.checkin_id;
            const checkinProps = {
                evento_id: participant.event_id || dataTable.evento_id,
                user_id: participant.user_id,
                ...(deletesSingleCheckin ? { id: checkinRecordId } : {}),
                type: "removeCheckin",
            };
            const checkinDeleteMessage = deletesSingleCheckin
                ? "Deseja remover apenas este registro de check-in?"
                : "Deseja remover todo o histórico de check-ins deste participante? Esta ação não pode ser desfeita.";

            return (
                <>
                    {canEdit && (
                    <OverlayTrigger overlay={<Tooltip>Excluir participante</Tooltip>}>
                        <Trash
                            role="button"
                            className="align-middle cursor-pointer me-2"
                            onClick={() => handleDelete(props, "Deseja remover esse participante? Todos os dados serão excluídos.")}
                            size={18}
                        />
                    </OverlayTrigger>
                    )}
                    {isCheckinsMode && canEdit && (participant.data_checkin || dataTable.json?.credentialAccess?.status) && (
                        participant.data_checkin ? (
                            checkinRecordId && (
                                <OverlayTrigger overlay={<Tooltip>{deletesSingleCheckin ? "Excluir este registro de check-in" : "Excluir todos os check-ins do participante"}</Tooltip>}>
                                    <RotateCcw
                                        role="button"
                                        className="align-middle cursor-pointer me-2"
                                        onClick={() => handleDelete(checkinProps, checkinDeleteMessage)}
                                        size={18}
                                    />
                                </OverlayTrigger>
                            )
                        ) : (
                            <OverlayTrigger overlay={<Tooltip>Realizar check-in</Tooltip>}>
                                <CheckCircle
                                    role="button"
                                    className="align-middle cursor-pointer me-2"
                                    onClick={() => handleCheckin(participant)}
                                    size={18}
                                />
                            </OverlayTrigger>
                        )
                    )}
                    <OverlayTrigger overlay={<Tooltip>Baixar Código QR</Tooltip>}>
                        <Download
                            role="button"
                            className="align-middle cursor-pointer me-2"
                            onClick={() => {
                                let code = createQRCodePlatform02(dataTable.token, cell.row.original)
                                downloadQRCode(code, cell.row.original.email);
                            }}
                            size={18}
                        />
                    </OverlayTrigger>
                    {canEdit && (
                    <OverlayTrigger overlay={<Tooltip>Editar participante</Tooltip>}>
                        <Edit
                            role="button"
                            className="align-middle cursor-pointer"
                            onClick={() => handleEdit(cell.row.original, 0)}
                            size={18}
                        />
                    </OverlayTrigger>
                    )}
                </>
            );
        },
    });

    setFields(newFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
}

export function setHeadersUsers(dataTable, setFieldsUsers, handleDelete, handleEdit, handleOpenCollectedParticipantsModal, filter, setShowImageModal, canEdit) {
    const newFields = [];
    const showEmailColumn =
        filter.extraPropsUsers || dataTable.json.form.showEmailInUserReport;
    if (filter.extraPropsUsers) {
        newFields.push({ Header: "ID", accessor: "user_id" });
        newFields.push({ Header: "ID Externo", accessor: "external_user_id" });
        newFields.push({
            Header: "Origem",
            accessor: "origin",
            Filter: SelectColumnFilter,
            filter: "equals",
            renderFilterLabel: getOriginLabel,
            Cell: ({ value }) => value ? getOriginLabel(value) : "—",
            exportValue: (_, value) => getOriginLabel(value) || "—",
        });
    }
    if (showEmailColumn) {
        newFields.push({ Header: dataTable.json.form.emailLabel ? dataTable.json.form.emailLabel : 'E-mail', accessor: "email" });
    }
    dataTable.json.form.fieldsUsers.forEach((element) => {
        if (!element.showInUserReport && !filter.extraPropsUsers) {
            return;
        }

        let maxLength = 40;
        let truncatedString = element.label.length > maxLength
            ? element.label.slice(0, maxLength - 3) + "..."
            : element.label;

        switch (element.type) {
            case 'textarea':
            case 'input':
                if (element.validation === 'number') {
                    newFields.push({
                        Header: truncatedString,
                        accessor: String(element.inputID),
                        Filter: NumberRangeColumnFilter,
                        filter: "between",
                    });
                } else {
                    newFields.push({
                        Header: truncatedString,
                        accessor: String(element.inputID),
                    });
                }
                break;
            case 'checkbox':
            case 'select':
                newFields.push({
                    Header: truncatedString,
                    accessor: String(element.inputID),
                    Filter: SelectColumnFilter,
                    filter: "equals",
                    Cell: ({ value }) => {
                        if (Array.isArray(value) && value.length > 0) {
                            return (
                                <div>
                                    {value.map((item, index) => (
                                        <div key={index} style={{ marginBottom: '2px', display: 'flex', alignItems: 'flex-start' }}>
                                            <span style={{ marginRight: '6px', fontWeight: 'bold' }}>&bull;</span>
                                            <span>{item}</span>
                                        </div>
                                    ))}
                                </div>
                            );
                        } else {
                            return value || '';
                        }
                    }
                });
                break;
            default:
                newFields.push({
                    Header: truncatedString,
                    accessor: String(element.inputID),
                });
                break;
        }
    });

    newFields.push({ Header: "Status", accessor: "status_formatted", Filter: SelectColumnFilter, filter: "equals" });
    newFields.push({ Header: "Tipo de usuário", accessor: "type_formatted" });
    newFields.push({ Header: "Expositor associado", accessor: "exhibitor_name" });
    newFields.push({ Header: "Total de participantes coletados", accessor: "total_users_collected" });

    newFields.push({ Header: "Data de criação", accessor: "creation_date_formatted" });
    newFields.push({ Header: "Data de registro", accessor: "register_date_formatted" });
    newFields.push({
        Header: "Foto de perfil", accessor: "image", Cell: (cell) => {
            if (cell.row.original.image) {
                return (
                    <a onClick={(e) => { e.preventDefault(); setShowImageModal({ url: cell.row.original.image, show: true }) }} href={cell.row.original.image} rel="noreferrer">Foto</a>
                )
            } else {
                return ""
            }
        }
    });
    if (filter.extraPropsUsers) {
        newFields.push({ Header: "Código de acesso", accessor: "access_code" });
        newFields.push({ Header: "Último acesso", accessor: "last_access_formatted" });
        newFields.push({ Header: "Último código de acesso", accessor: "access_code_used" });
        newFields.push({ Header: "Expiração do código de acesso", accessor: "access_code_expires_formatted" });
        newFields.push({ Header: "Notificação", accessor: "push_subscription" });
    }
    newFields.push({
        Header: "",
        accessor: 'icons',
        disableSortBy: true,
        Filter: false,
        Cell: (cell) => {
            const props = buildPlatform2UserAction({
                dataTable,
                action: PLATFORM_2_USER_ACTIONS.DELETE,
                userId: cell.row.original.user_id,
                userType: cell.row.original.type,
            });
            return (
                <>
                    {canEdit && (
                    <OverlayTrigger overlay={<Tooltip>Excluir usuário</Tooltip>}>
                        <Trash
                            role="button"
                            className="align-middle cursor-pointer mx-1"
                            onClick={() => {
                                handleDelete(
                                    props,
                                    cell.row.original.exhibitor_id
                                        ? "Deseja remover esse usuário? A associação com o expositor também será removida. Todos os dados serão excluídos."
                                        : "Deseja remover esse usuário? Todos os dados serão excluídos.",
                                )
                            }}
                            size={18}
                        />
                    </OverlayTrigger>
                    )}
                    <OverlayTrigger overlay={<Tooltip>Ver participantes coletados</Tooltip>}>
                        <Users
                            role="button"
                            className="align-middle cursor-pointer mx-1"
                            onClick={() => handleOpenCollectedParticipantsModal(cell.row.original)}
                            size={18}
                        />
                    </OverlayTrigger>
                    {cell.row.original.exhibitor_id && (
                    <OverlayTrigger overlay={<Tooltip>Baixar Código QR</Tooltip>}>
                        <Download
                            role="button"
                            className="align-middle cursor-pointer mx-1"
                            onClick={() => {
                                let code = createQRCodePlatform02(dataTable.token, cell.row.original)
                                downloadQRCode(
                                    code,
                                    cell.row.original.exhibitor_name || cell.row.original.email,
                                );
                            }}
                            size={18}
                        />
                    </OverlayTrigger>
                    )}
                    {canEdit && (
                    <OverlayTrigger overlay={<Tooltip>Editar usuário</Tooltip>}>
                        <Edit
                            role="button"
                            className="align-middle cursor-pointer mx-1"
                            onClick={() => handleEdit(cell.row.original, 1)}
                            size={18}
                        />
                    </OverlayTrigger>
                    )}
                </>
            );
        },
    });
    setFieldsUsers(newFields);
}

export function setHeadersQuiz(dataTable, selectedActivity, setTableColumnsResults, deleteActivityHistory, filter) {
    const newFields = [];
    if (filter.extraProps || dataTable.json.form.showEmailInUserReport) {
        newFields.push({ Header: dataTable.json.form.emailLabel ? dataTable.json.form.emailLabel : 'E-mail', accessor: "email" });
    }
    newFields.push({
        Header: "Pontuação",
        accessor: "score",
        Filter: NumberRangeColumnFilter,
        filter: "between",
    });
    newFields.push({
        Header: "Tempo", accessor: "time"
    });
    newFields.push({
        Header: "Questão",
        accessor: "currentQuestion",
        Filter: SelectColumnFilter,
        filter: "equals"
    });

    if (filter.extraProps) {
        selectedActivity.json.questions.forEach(element => {
            newFields.push({
                Header: element.question, accessor: 'answer' + element.question_id,
                Filter: SelectColumnFilter,
                filter: "equals"
            });
        });
    }

    newFields.push({
        Header: "",
        accessor: 'icons',
        disableSortBy: true,
        Filter: false,
        Cell: (cell) => {
            return (
                <>
                    <OverlayTrigger overlay={<Tooltip>Excluir</Tooltip>}>
                        <Trash
                            role="button"
                            className="align-middle cursor-pointer me-2"
                            onClick={() => deleteActivityHistory(cell.row.original.activity_id, cell.row.original.user_id)}
                            size={18}
                        />
                    </OverlayTrigger>
                </>
            );
        },
    });
    setTableColumnsResults(newFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
}

export function generateAccessData02(entries) {
    if (!entries) {
        return ""
    }
    const today = new Date();
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const daysInMonth = endOfMonth.getDate();
    const dataCounts = Array(daysInMonth).fill(0);

    // Generate dates for 7 days before and after today
    const dateRange = [];
    for (let i = 1; i <= daysInMonth; i++) {
        const newDate = new Date(today.getFullYear(), today.getMonth(), i);
        dateRange.push(newDate);
    }

    // Count the number of accesses for each day in the ranges
    entries.forEach(entry => {
        for (let i = 0; i < dateRange.length; i++) {
            let newDateEntry
            if (entry.data_visitacao) {
                newDateEntry = parseFlexibleDate(entry.data_visitacao);
            } else {
                newDateEntry = parseFlexibleDate(entry.creation_date);
            }
            if (newDateEntry.toDateString() === dateRange[i].toDateString()) {
                dataCounts[i]++;
                break;
            }
        }
    });
    const series = [
        {
            name: 'Total de registros',
            data: dataCounts
        }
    ];
    return series
}

export const sortRanking = (data) => {
    data.sort((a, b) => {
        const currentIndexA = a.currentIndex || 0;
        const currentIndexB = b.currentIndex || 0;
        const scoreA = a.score || 0;
        const scoreB = b.score || 0;
        const timeA = a.timeX || Infinity; // Set default to Infinity instead of 0
        const timeB = b.timeX || Infinity; // Set default to Infinity instead of 0
        if (currentIndexA !== currentIndexB) {
            return currentIndexB - currentIndexA;
        }

        if (scoreA !== scoreB) {
            return scoreB - scoreA;
        }
        if (timeA !== timeB) {
            return timeA - timeB;
        }
        return 0
    });
    return data
}

export const ModalCollectedParticipants = ({ showModal, setShowModal, selectedUser = {}, dataTable, handleExportResult }) => {
    const [collectedParticipants, setCollectedParticipants] = useState([]);
    const [loading, setLoading] = useState(false);
    const notyf = useContext(NotyfContext);
    const collectedParticipantFields = getCollectedParticipantFields(dataTable);

    useEffect(() => {
        if (showModal && showModal.show && selectedUser.user_id) {
            setLoading(true);
            axios.get(`/api/event/plataformaEventos/collectedParticipants/${selectedUser.user_id}?event_id=${dataTable.event_id}&user_type=${selectedUser.type}`)
                .then(function (response) {
                    setCollectedParticipants(Array.isArray(response.data) ? response.data : []);
                    setLoading(false);
                })
                .catch(function (error) {
                    setLoading(false);
                    notyf.open({
                        type: "danger",
                        message: "Erro ao carregar participantes coletados",
                        ripple: true,
                        dismissible: true,
                        });
                });
        }
    }, [showModal, selectedUser.user_id, selectedUser.type, dataTable.event_id, notyf]);

    const handleClose = () => {
        setShowModal({ show: false });
        setCollectedParticipants([]);
    };

    const handleExport = async () => {
        if (collectedParticipants.length > 0) {
            const headers = getCollectedParticipantsExportHeaders(dataTable.json?.form?.fields, {
                includeHiddenFields: true,
            });
            try {
                await getExcelFromObject({
                    filename: dataTable.title + ' - ' + selectedUser.email + ' - Participantes Coletados',
                    headers,
                    data: collectedParticipants,
                });
            } catch (error) {
                notyf.open({
                    type: "danger",
                    message: "Não foi possível gerar o arquivo de exportação",
                    ripple: true,
                    dismissible: true,
                });
                return;
            }
        } else {
            notyf.open({
                type: "danger",
                message: "Nenhum participante coletado",
                ripple: true,
                dismissible: true,
            });
        }
        handleClose();
    };

    return (
        <Modal
            show={showModal && showModal.show}
            onHide={handleClose}
            size="lg"
            centered
            backdrop="static"
            keyboard={false}
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    Participantes Coletados ({collectedParticipants.length} participante{collectedParticipants.length !== 1 ? 's' : ''})
                    <br />
                    <small className="text-muted"><b>Usuário:</b> {selectedUser.email || 'N/A'}</small>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {loading ? (
                    <div className="py-5 text-center">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Carregando...</span>
                        </div>
                    </div>
                ) : collectedParticipants.length > 0 ? (
                    <Row>
                        {collectedParticipants.map((participant, index) => (
                            <Col md={6} className="mb-3" key={`participant-${index}`}>
                                <Card className="h-100">
                                    <Card.Body>
                                        <div className="d-flex align-items-start">
                                            {participant.image && (
                                                <img
                                                    src={participant.image}
                                                    alt="Profile"
                                                    className="rounded-circle me-3"
                                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                />
                                            )}
                                            <div className="flex-grow-1">
                                                <h6 className="mb-1">{participant.email}</h6>
                                                {getCollectedParticipantDisplayFields(participant, collectedParticipantFields).map(({ inputID, label, displayValue }) => (
                                                    <small key={`participant-field-${inputID}`} className="text-muted d-block">
                                                        <strong>{label}:</strong> {displayValue}
                                                    </small>
                                                ))}
                                                <small className="text-muted d-block mt-2">
                                                    <strong>Coletado em:</strong> {participant.date_collected}
                                                </small>
                                                {participant.total_value && (
                                                    <small className="text-muted d-block">
                                                        <strong>Pontuação:</strong> {participant.total_value}
                                                    </small>
                                                )}
                                                {participant.rank_position && (
                                                    <small className="text-muted d-block">
                                                        <strong>Ranking:</strong> #{participant.rank_position}
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                ) : (
                    <div className="text-center py-4">
                        <p className="text-muted">Nenhum participante coletado encontrado.</p>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Fechar
                </Button>
                <Button variant="primary" onClick={handleExport} disabled={loading}>
                    Exportar Lista
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
